import { useState, useEffect } from 'react';

import {
  Container,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Stack,
  Grid,
  Button,
} from '@mui/material';

import LoadingMask from '@/components/shared/LoadingMask';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import { useUser, useUserDispatch } from '@/providers/UserContext';
import { useConfiguration, useConfigurationDispatch } from '@/providers/ConfigurationContext';
import { USER_FETCH, SET_GENERATION_CONFIG } from '@/providers/actionTypes';
import AccountContactForm from './AccountContactForm';
import AccountKeys from './AccountKeys';
import AccountChatSettings from './AccountChatSettings';
import AccountActions from './AccountActions';
import FormUpdateButton from './FormUpdateButton';
import { updateUser } from '@/actions/user';
import { FormErrorProvider } from '@/providers/FormErrorContext';
import { useApi } from '@/providers/ApiContext';
import type { AccountSection, UserUpdate } from '@/types';

function Account() {
  const user = useUser();
  const api = useApi();
  const configuration = useConfiguration();
  const configuration_dispatch = useConfigurationDispatch();
  const [revision, setRevision] = useState<number>(0);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [userUpdate, setUserUpdate] = useState<UserUpdate>({});
  const [chat_settings_revision, setChatSettingsRevision] = useState<number>(0);
  const [max_new_tokens, setMaxNewTokens] = useState<number>(
    configuration?.generation?.max_new_tokens ?? 0
  );
  const [temperature, setTemperature] = useState<number>(
    configuration?.generation?.temperature ?? 0
  );
  const [top_p, setTopP] = useState<number>(configuration?.generation?.top_p ?? 0);
  const [typical_p, setTypicalP] = useState<number>(
    configuration?.generation?.typical_p ?? 0
  );

  const userDispatch = useUserDispatch();

  useEffect(() => {
    if (configuration?.generation) {
      setMaxNewTokens(configuration.generation.max_new_tokens ?? 0);
      setTemperature(configuration.generation.temperature ?? 0);
      setTopP(configuration.generation.top_p ?? 0);
      setTypicalP(configuration.generation.typical_p ?? 0);
    }
  }, [configuration?.generation]);

  const handleChatSettingsUpdate = () => {
    configuration_dispatch?.({
      type: SET_GENERATION_CONFIG,
      payload: { max_new_tokens, temperature, top_p, typical_p },
    });
    setChatSettingsRevision((prev) => prev + 1);
  };

  const handleChatSettingsReset = () => {
    if (configuration?.generation) {
      setMaxNewTokens(configuration.generation.max_new_tokens ?? 0);
      setTemperature(configuration.generation.temperature ?? 0);
      setTopP(configuration.generation.top_p ?? 0);
      setTypicalP(configuration.generation.typical_p ?? 0);
      setChatSettingsRevision((prev) => prev + 1);
    }
  };

  const handleUserUpdate = async () => {
    if (!api || !user) return;
    setLoading(true);
    const payload = Object.fromEntries(
      Object.entries({ ...user, ...userUpdate }).filter(
        ([_k, v]) => v !== undefined && v !== '',
      ),
    );
    const updated_user = await updateUser(api, payload);
    userDispatch({ type: USER_FETCH, payload: updated_user });
    setLoading(false);
  };

  useEffect(() => {
    setRevision((prevState) => prevState + 1);
  }, [user]);

  const sections: AccountSection[] = [
    {
      id: 'contact',
      label: 'Contact Info',
      component: (
        <FormErrorProvider>
          <AccountContactForm
            key={`contact_info_${revision}`}
            setValues={setUserUpdate}
          />
        </FormErrorProvider>
      ),
      icon: <PersonOutlineOutlinedIcon />,
      actions: (
        <FormErrorProvider>
          <FormUpdateButton
            handleUpdate={handleUserUpdate}
            loading={loading}
            label='Update'
          />
        </FormErrorProvider>
      ),
    },
    {
      id: 'chat-settings',
      label: 'Chat Settings',
      component: (
        <AccountChatSettings
          key={`chat_settings_${chat_settings_revision}`}
          max_new_tokens={max_new_tokens}
          setMaxNewTokens={setMaxNewTokens}
          temperature={temperature}
          setTemperature={setTemperature}
          top_p={top_p}
          setTopP={setTopP}
          typical_p={typical_p}
          setTypicalP={setTypicalP}
          revision={chat_settings_revision}
        />
      ),
      icon: <ChatOutlinedIcon />,
      actions: (
        <Stack direction='row' spacing={1}>
          <Button variant='outlined' color='inherit' onClick={handleChatSettingsReset}>
            Reset
          </Button>
          <Button variant='outlined' color='primary' onClick={handleChatSettingsUpdate}>
            Update
          </Button>
        </Stack>
      ),
    },
    {
      id: 'keys',
      label: 'API Keys',
      component: <AccountKeys key={`keys_${revision}`} />,
      icon: <VpnKeyOutlinedIcon />,
    },
    {
      id: 'account',
      label: 'Account',
      component: <AccountActions key={`actions_${revision}`} />,
      icon: <SettingsOutlinedIcon />,
    },
  ];

  if (!user?.entity_id) {
    return <LoadingMask />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Container sx={{ padding: '15px' }}>
        <Grid container spacing={4}>
          <Grid size={12}>
            <Typography variant='h4'>My Account</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            {/* <Paper> */}
            <List>
              {sections.map((section, index) => (
                <ListItemButton
                  onClick={() => setSelectedIndex(index)}
                  key={section.id}
                  selected={index === selectedIndex}
                >
                  <ListItemIcon>{section.icon}</ListItemIcon>
                  <ListItemText primary={section.label} />
                </ListItemButton>
              ))}
            </List>
            {/* </Paper> */}
          </Grid>
          <Grid size={{ xs: 12, md: 8, lg: 8 }}>
            <Stack direction='column'>
              {sections[selectedIndex].component}
              {sections[selectedIndex].actions ? (
                <Box width='100%' padding='15px' paddingLeft={'30px'}>
                  {sections[selectedIndex].actions}
                </Box>
              ) : undefined}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Account;

